import {notifications} from '@mantine/notifications';
import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import type {z} from 'zod';
import type {TodoCandidate} from '../../../core/entities/entryTodos/schema';
import {
  useAiGenerateEntryTodos,
  useCreateEntryTodo,
} from '../../../core/repositories/entryTodosRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components';
import {CACHE_KEYS} from './useEntryManagementUpdate';

/**
 * 通知メッセージ定数
 */
const NOTIFICATION_MESSAGES = {
  GENERATION_IN_PROGRESS: {
    ID: 'ai-todo-generation',
    TITLE: 'AI ToDo生成中',
    MESSAGE:
      '選考予定に関連するToDoを自動生成しています... 結果を表示するにはページに留まってください',
  },
  SAVE_SUCCESS: {
    TITLE: 'ToDo保存完了',
    MESSAGE: 'ToDoを保存しました',
  },
  SAVE_FAILURE: {
    TITLE: 'ToDo保存失敗',
    MESSAGE: 'ToDoの保存に失敗しました。内容を確認してください。',
    MESSAGE_NETWORK:
      'ネットワークエラーが発生しました。接続を確認してください。',
    MESSAGE_GENERIC:
      'ToDoの保存に失敗しました。しばらくしてから再度お試しください。',
  },
  SAVE_ALL_SUCCESS: {
    TITLE: 'ToDo保存完了',
  },
  SAVE_ALL_FAILURE: {
    TITLE: 'ToDo保存失敗',
    MESSAGE:
      'すべてのToDoの保存に失敗しました。しばらくしてから再度お試しください。',
  },
  SAVE_ALL_PARTIAL: {
    TITLE: 'ToDo保存結果',
  },
  ERROR: {
    TITLE: 'エラー',
    NO_STEP_ID: 'ステップIDが見つかりません',
    NO_TODOS: '保存するToDoがありません',
  },
} as const;

/**
 * ログプレフィックス定数
 */
const LOG_PREFIX = {
  GENERATION: '[AI ToDo Generation]',
  SAVE: '[AI ToDo Save]',
  SAVE_ALL: '[AI ToDo Save All]',
  UPDATE: '[AI ToDo Update]',
} as const;

/**
 * TODO候補のユニークIDを生成
 * @param index 候補のインデックス
 * @returns ユニークなID文字列
 */
export const generateCandidateId = (index: number): string => {
  return `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 11)}`;
};

export type TodoCandidateType = z.infer<typeof TodoCandidate>;

/** IDを持つToDo候補（モーダル表示用） */
export type TodoCandidateWithId = TodoCandidateType & {
  candidateId: string;
};

type SavedTodoState = {
  index: number;
  todoId: number;
};

/**
 * ネットワークエラーかどうかを判定
 */
const isNetworkError = (error: unknown): boolean => {
  return error instanceof Error && error.message.includes('Network');
};

/**
 * 一括保存結果のメッセージを生成
 */
const getBulkSaveResultMessage = (
  successCount: number,
  failCount: number,
): string => {
  return `${successCount}件保存、${failCount}件失敗しました。失敗したToDoは再度お試しください。`;
};

/**
 * AI TODO生成機能を提供するカスタムフック
 */
export const useAiTodoGeneration = () => {
  const queryClient = useQueryClient();
  const aiGenerateMutation = useAiGenerateEntryTodos();
  const createTodoMutation = useCreateEntryTodo();

  // 結果モーダル関連の状態
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [candidates, setCandidates] = useState<TodoCandidateWithId[]>([]);
  const [savedTodos, setSavedTodos] = useState<SavedTodoState[]>([]);
  const [stepID, setStepID] = useState<number | null>(null);
  const [userEntryManagementID, setUserEntryManagementID] = useState<
    number | null
  >(null);

  // 保存状態管理
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [isSavingAll, setIsSavingAll] = useState(false);

  // 編集状態管理
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingCandidate, setEditingCandidate] =
    useState<TodoCandidateWithId | null>(null);

  // 選択モーダル用の状態
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [pendingStepID, setPendingStepID] = useState<number | null>(null);
  const [pendingStepName, setPendingStepName] = useState<string>('');
  const [pendingUserEntryManagementID, setPendingUserEntryManagementID] =
    useState<number | null>(null);

  /**
   * 選択モーダルを開く
   */
  const openSelectModal = (
    newStepID: number,
    newUserEntryManagementID?: number,
    stepName?: string,
  ) => {
    setPendingStepID(newStepID);
    setPendingStepName(stepName ?? '');
    setPendingUserEntryManagementID(newUserEntryManagementID ?? null);
    setIsSelectModalOpen(true);
  };

  /**
   * 選択モーダルを閉じる
   * 生成中に閉じた場合はスナックバーを表示
   */
  const closeSelectModal = () => {
    if (aiGenerateMutation.isLoading) {
      // ローディング通知表示（loading状態で表示し続ける）
      notifications.show({
        id: NOTIFICATION_MESSAGES.GENERATION_IN_PROGRESS.ID,
        title: NOTIFICATION_MESSAGES.GENERATION_IN_PROGRESS.TITLE,
        message: NOTIFICATION_MESSAGES.GENERATION_IN_PROGRESS.MESSAGE,
        color: 'blue',
        loading: true,
        autoClose: false,
      });
    } else {
      // 生成中でなければpending状態をクリア
      setPendingStepID(null);
      setPendingUserEntryManagementID(null);
    }
    setIsSelectModalOpen(false);
  };

  /**
   * pending状態をクリア
   */
  const clearPendingState = () => {
    setPendingStepID(null);
    setPendingStepName('');
    setPendingUserEntryManagementID(null);
  };

  /**
   * 生成成功時の処理
   */
  const handleGenerationSuccess = (todos: TodoCandidateType[]) => {
    if (pendingStepID === null) return;

    // 各候補にユニークIDを付与
    const candidatesWithId: TodoCandidateWithId[] = todos.map(
      (todo, index) => ({
        ...todo,
        candidateId: generateCandidateId(index),
      }),
    );
    setCandidates(candidatesWithId);
    setStepID(pendingStepID);
    setUserEntryManagementID(pendingUserEntryManagementID);
    setSavedTodos([]);
    setSavingIndex(null);
    setIsModalOpen(true);
  };

  /**
   * AI TODO生成を実行（非同期・Silent Fail）
   * ローディング中はモーダルでローダーを表示
   */
  const generateTodos = async (params: {
    departmentID?: number;
    selectionPhaseID?: number;
  }) => {
    if (pendingStepID === null) return;

    // 選択モーダルは開いたまま（ローディング表示のため）
    // isGenerating状態はaiGenerateMutation.isLoadingで管理される

    try {
      const result = await aiGenerateMutation.mutateAsync({
        stepID: pendingStepID,
        departmentID: params.departmentID,
        selectionPhaseID: params.selectionPhaseID,
      });

      // 生成完了時にスナックバーを閉じる
      notifications.hide(NOTIFICATION_MESSAGES.GENERATION_IN_PROGRESS.ID);

      // 選択モーダルを閉じる
      setIsSelectModalOpen(false);

      if (result.ok && result.value.todos.length > 0) {
        // 成功時: 結果モーダル表示
        handleGenerationSuccess(result.value.todos);
      }
      // 候補が0件の場合は何も表示しない（Silent Fail）

      // pending状態をクリア
      clearPendingState();
    } catch (error) {
      // 生成完了時にスナックバーを閉じる
      notifications.hide(NOTIFICATION_MESSAGES.GENERATION_IN_PROGRESS.ID);

      // Silent Fail: エラー通知なし
      // デバッグ用にエラーログを出力
      console.error(
        `${LOG_PREFIX.GENERATION} Error during todo generation:`,
        error,
      );
      // 選択モーダルを閉じる
      setIsSelectModalOpen(false);
      // pending状態をクリア
      clearPendingState();
    }
  };

  /**
   * TODOキャッシュを無効化
   */
  const invalidateTodoCache = async () => {
    await queryClient.invalidateQueries(['entryTodos']);
    await queryClient.invalidateQueries([CACHE_KEYS.PROGRESS_MISSIONS]);
  };

  /**
   * TODO候補を個別保存
   */
  const saveTodo = async (candidate: TodoCandidateType, index: number) => {
    if (stepID === null) {
      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.ERROR.TITLE,
        message: NOTIFICATION_MESSAGES.ERROR.NO_STEP_ID,
      });
      return false;
    }

    setSavingIndex(index);

    try {
      const result = await createTodoMutation.mutateAsync({
        userEntryManagementID: userEntryManagementID ?? undefined,
        userEntryManagementStepID: stepID,
        title: candidate.title,
        link: candidate.link,
        body: candidate.body,
        dueDate: candidate.dueDate ?? undefined,
        priority: candidate.priority ?? undefined,
        isAiGenerated: true,
      });

      if (result.ok) {
        // 保存成功
        setSavedTodos((prev) => [...prev, {index, todoId: result.value.id}]);

        // キャッシュ無効化
        await invalidateTodoCache();

        showSuccessSnackBar({
          title: NOTIFICATION_MESSAGES.SAVE_SUCCESS.TITLE,
          message: NOTIFICATION_MESSAGES.SAVE_SUCCESS.MESSAGE,
        });

        setSavingIndex(null);
        return true;
      }

      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.SAVE_FAILURE.TITLE,
        message: NOTIFICATION_MESSAGES.SAVE_FAILURE.MESSAGE,
      });
      setSavingIndex(null);
      return false;
    } catch (error) {
      console.error(`${LOG_PREFIX.SAVE} Error saving todo:`, error);
      const errorMessage = isNetworkError(error)
        ? NOTIFICATION_MESSAGES.SAVE_FAILURE.MESSAGE_NETWORK
        : NOTIFICATION_MESSAGES.SAVE_FAILURE.MESSAGE_GENERIC;

      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.SAVE_FAILURE.TITLE,
        message: errorMessage,
      });
      setSavingIndex(null);
      return false;
    }
  };

  /**
   * 特定のTODOが保存済みかチェック
   */
  const isTodoSaved = (index: number): boolean => {
    return savedTodos.some((saved) => saved.index === index);
  };

  /**
   * 未保存のインデックスを取得
   */
  const getUnsavedIndices = (): number[] => {
    return candidates
      .map((_, index) => index)
      .filter((index) => !isTodoSaved(index));
  };

  /**
   * 一括保存の結果通知を表示
   */
  const showBulkSaveResultNotification = (
    successCount: number,
    failCount: number,
  ) => {
    if (failCount === 0) {
      showSuccessSnackBar({
        title: NOTIFICATION_MESSAGES.SAVE_ALL_SUCCESS.TITLE,
        message: `${successCount}件のToDoを保存しました`,
      });
    } else if (successCount === 0) {
      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.SAVE_ALL_FAILURE.TITLE,
        message: NOTIFICATION_MESSAGES.SAVE_ALL_FAILURE.MESSAGE,
      });
    } else {
      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.SAVE_ALL_PARTIAL.TITLE,
        message: getBulkSaveResultMessage(successCount, failCount),
      });
    }
  };

  /**
   * 未保存のTODO候補を一括保存
   */
  const saveAllTodos = async () => {
    if (stepID === null) {
      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.ERROR.TITLE,
        message: NOTIFICATION_MESSAGES.ERROR.NO_STEP_ID,
      });
      return;
    }

    setIsSavingAll(true);

    // 未保存の候補を抽出
    const unsavedIndices = getUnsavedIndices();

    if (unsavedIndices.length === 0) {
      showErrorSnackBar({
        title: NOTIFICATION_MESSAGES.ERROR.TITLE,
        message: NOTIFICATION_MESSAGES.ERROR.NO_TODOS,
      });
      setIsSavingAll(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    // 未保存のTODOを順次保存
    for (const index of unsavedIndices) {
      setSavingIndex(index);

      const candidate = candidates[index];
      if (!candidate) {
        failCount++;
        continue;
      }

      try {
        const result = await createTodoMutation.mutateAsync({
          userEntryManagementID: userEntryManagementID ?? undefined,
          userEntryManagementStepID: stepID,
          title: candidate.title,
          link: candidate.link,
          body: candidate.body,
          dueDate: candidate.dueDate ?? undefined,
          priority: candidate.priority ?? undefined,
          isAiGenerated: true,
        });

        if (result.ok) {
          // 保存成功
          setSavedTodos((prev) => [...prev, {index, todoId: result.value.id}]);
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`${LOG_PREFIX.SAVE_ALL} Error saving todo:`, error);
        failCount++;
      }
    }

    setSavingIndex(null);
    setIsSavingAll(false);

    // キャッシュ無効化
    await invalidateTodoCache();

    // 結果通知
    showBulkSaveResultNotification(successCount, failCount);
  };

  /**
   * 結果モーダルを閉じる
   */
  const closeModal = () => {
    setIsModalOpen(false);
    setCandidates([]);
    setStepID(null);
    setUserEntryManagementID(null);
    setSavedTodos([]);
    setSavingIndex(null);
    setEditingIndex(null);
    setEditingCandidate(null);
  };

  /**
   * TODO候補の編集を開始
   */
  const startEdit = (index: number) => {
    const candidate = candidates[index];
    if (!candidate) return;

    setEditingIndex(index);
    setEditingCandidate({...candidate});
  };

  /**
   * TODO候補の編集をキャンセル
   */
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingCandidate(null);
  };

  /**
   * TODO候補を更新（編集内容を確定）
   */
  const updateCandidate = (index: number, updated: TodoCandidateWithId) => {
    // 編集中のインデックスと一致する場合のみ更新
    if (editingIndex !== index) {
      console.warn(
        `${LOG_PREFIX.UPDATE} Cannot update candidate ${index} - currently editing ${editingIndex}`,
      );
      return;
    }

    setCandidates((prev) =>
      prev.map((candidate, i) => (i === index ? updated : candidate)),
    );
    setEditingIndex(null);
    setEditingCandidate(null);
  };

  return {
    // 選択モーダル関連
    openSelectModal,
    closeSelectModal,
    isSelectModalOpen,
    pendingStepName,
    // 生成・保存関連
    generateTodos,
    saveTodo,
    saveAllTodos,
    closeModal,
    isTodoSaved,
    startEdit,
    cancelEdit,
    updateCandidate,
    isModalOpen,
    candidates,
    isGenerating: aiGenerateMutation.isLoading,
    isSaving: savingIndex !== null,
    isSavingAll,
    savingIndex,
    editingIndex,
    editingCandidate,
  };
};
